import React, { useState, useEffect } from "react";
import { LegendOrdinal } from "@visx/legend";
import { scaleOrdinal } from "@visx/scale";

export interface LegendItem {
  label: string;
  value: number;
  color: string;
  showValue?: boolean;
  isDashed?: boolean;
  /** Shorter text in the UI; `label` is used for scale identity and hover `title`. */
  displayLabel?: string;
}

interface ChartLegendProps {
  items: LegendItem[];
  position?: "right" | "bottom";
  mode?: "stacked" | "inline";
  className?: string;
  showValues?: boolean;
  isDarkMode?: boolean;
  /** Max lines per label before ellipsis (stacked / bottom legends). */
  labelLineClamp?: number;
  /** When true, map legend rows to `items` by index instead of matching `label`. */
  matchItemsByIndex?: boolean;
  /** When true, set `title` on each label to the full `item.label`. */
  showFullLabelTitle?: boolean;
}

function legendLabelKey(text: string): string {
  return text.includes(" (") ? text.split(" (")[0] : text;
}

function findLegendItem(items: LegendItem[], labelText: string): LegendItem | undefined {
  const key = legendLabelKey(labelText);
  return items.find((item) => item.label === key);
}

export const ChartLegend: React.FC<ChartLegendProps> = ({
  items,
  position = "right",
  mode = "inline",
  className = "",
  showValues = true,
  isDarkMode: propIsDarkMode,
  labelLineClamp,
  matchItemsByIndex = false,
  showFullLabelTitle = false,
}) => {
  // State to track current theme with reactive updates
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Use prop if provided, otherwise check localStorage
    if (propIsDarkMode !== undefined) return propIsDarkMode;

    const storedTheme = localStorage.getItem("theme");
    if (storedTheme) {
      return storedTheme === "dark";
    }
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });

  // Effect to listen for theme changes
  useEffect(() => {
    // If prop is provided, use that value directly and don't listen for changes
    if (propIsDarkMode !== undefined) {
      setIsDarkMode(propIsDarkMode);
      return;
    }

    const updateTheme = () => {
      const storedTheme = localStorage.getItem("theme");
      if (storedTheme) {
        setIsDarkMode(storedTheme === "dark");
      } else {
        setIsDarkMode(
          window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
        );
      }
    };

    // Listen for storage events (when localStorage changes)
    window.addEventListener("storage", updateTheme);

    // Setup MutationObserver to detect theme changes via DOM attributes
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.attributeName === "data-theme" ||
          mutation.attributeName === "class"
        ) {
          updateTheme();
        }
      }
    });

    // Start observing document for theme changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      window.removeEventListener("storage", updateTheme);
      observer.disconnect();
    };
  }, [propIsDarkMode]);

  // Create a scale for the legend
  const ordinalScale = scaleOrdinal({
    domain: items.map((item) => item.label),
    range: items.map((item) => item.color),
  });

  // Determine layout based on position and mode
  const isStacked = mode === "stacked";
  const isBottom = position === "bottom";

  // Set theme-specific colors
  const textColor = isDarkMode ? "#e2e8f0" : "#334155"; // Light gray for dark mode, dark gray for light mode
  const textColorSecondary = isDarkMode ? "#94a3b8" : "#64748b"; // Lighter gray for dark mode, medium gray for light mode

  const legendStyles = {
    display: "flex",
    flexDirection: isStacked ? "column" : "row",
    flexWrap: isStacked ? "nowrap" : "wrap",
    gap: "8px",
    width: "100%",
    justifyContent: isBottom ? "center" : "flex-start",
    color: textColor,
  } as React.CSSProperties;

  return (
    <div className={`chart-legend ${className}`} style={legendStyles}>
      <LegendOrdinal
        scale={ordinalScale}
        labelFormat={(label) => {
          const item = items.find((i) => i.label === label);
          const display = item?.displayLabel ?? label;
          // Only append the value if showValues is true and the item is configured to show its value
          const shouldShowValue = showValues && item?.showValue !== false;
          return shouldShowValue && item
            ? `${display} (${item.value.toFixed(1)}%)`
            : display;
        }}
      >
        {(labels) => (
          <div
            style={{
              display: "flex",
              flexDirection: isStacked ? "column" : "row",
              flexWrap: isStacked ? "nowrap" : "wrap",
              gap: "8px",
              fontSize: "12px",
              justifyContent: isBottom ? "center" : "flex-start",
              width: "100%",
            }}
          >
            {labels.map((label, i) => {
              const originalItem = matchItemsByIndex
                ? items[i]
                : findLegendItem(items, label.text);

              return (
                <div
                  key={`legend-${i}`}
                  style={{
                    display: "flex",
                    alignItems:
                      isStacked && labelLineClamp ? "flex-start" : "center",
                    marginRight: isStacked ? "0" : "8px",
                    marginBottom: isStacked ? "4px" : "0",
                    cursor: "pointer",
                    width: isStacked ? "100%" : "auto",
                    justifyContent: isStacked ? "space-between" : "flex-start",
                    color: textColor,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <svg width={15} height={15} style={{ marginRight: "4px" }}>
                      <circle
                        fill={
                          originalItem?.isDashed ? "transparent" : label.value
                        }
                        stroke={originalItem?.isDashed ? label.value : "none"}
                        strokeWidth={originalItem?.isDashed ? 2 : 0}
                        strokeDasharray={
                          originalItem?.isDashed ? "4,2" : "none"
                        }
                        r={6}
                        cx={7.5}
                        cy={7.5}
                      />
                    </svg>
                    <span
                      style={{
                        color: textColor,
                        ...(labelLineClamp
                          ? {
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: labelLineClamp,
                              WebkitBoxOrient: "vertical" as const,
                              lineHeight: 1.25,
                              wordBreak: "break-word",
                            }
                          : {}),
                      }}
                      title={
                        showFullLabelTitle
                          ? (originalItem?.label ?? label.text)
                          : undefined
                      }
                    >
                      {label.text.includes(" (")
                        ? label.text.split(" (")[0]
                        : label.text}
                    </span>
                  </div>
                  {isStacked && showValues && (
                    <span
                      className="font-medium text-right"
                      style={{ color: textColorSecondary }}
                    >
                      {(matchItemsByIndex
                        ? items[i]
                        : findLegendItem(items, label.text)
                      )?.value.toFixed(1)}
                      %
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </LegendOrdinal>
    </div>
  );
};
